import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search, Plus, Edit, Eye, Trash2, Users, Phone } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { branchsAPI } from '@/services/branchs';
import { Pagination } from '@/components/shared/Pagination';
import { useNavigate } from 'react-router-dom';
import { ImageWithFallback } from '@/components/ImageWithFallback';
import { DeleteConfirmationModal } from '@/components/modals/DeleteConfirmationModal';

export function BranchManagement() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [branchToDelete, setBranchToDelete] = useState<string | null>(null);

  // Fetch branches
  const {
    data: branchesData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [
      'branches',
      {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm,
        isActive: statusFilter === 'active',
      },
    ],
    queryFn: () =>
      branchsAPI.getBranches({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        isActive: statusFilter === 'active' ? true : undefined,
      }),
  });

  // Filter branches based on search and status
  const filteredBranches =
    branchesData?.data?.filter((branch) => {
      const matchesSearch = branch.name
        ?.toLowerCase()
        .includes(searchTerm?.toLowerCase() || '');
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && branch.isActive) ||
        (statusFilter === 'inactive' && !branch.isActive);
      return matchesSearch && matchesStatus;
    }) || [];

  const handleFilterChange = (callback: () => void) => {
    setCurrentPage(1);
    callback();
  };

  // Handle branch actions
  const queryClient = useQueryClient();

  // Delete branch mutation
  const deleteBranchMutation = useMutation({
    mutationFn: (branchId: string) => branchsAPI.deleteBranch(branchId),
    onSuccess: () => {
      toast.success('Branch deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['branches'] });
      setBranchToDelete(null);
    },
    onError: (error) => {
      toast.error('Failed to delete branch');
      console.error('Error deleting branch:', error);
    },
  });

  const handleBranchAction = (branch: any, action: string) => {
    if (action === 'view') {
      navigate(`/branch-management/${branch.id}`);
    } else if (action === 'edit') {
      navigate(`/branch-management/edit/${branch.id}`);
    } else if (action === 'delete' && branch) {
      setBranchToDelete(branch.id);
    } else if (action === 'add') {
      navigate('/branch-management/new');
    }
  };

  const handleConfirmDelete = () => {
    if (branchToDelete) {
      deleteBranchMutation.mutate(branchToDelete);
    }
  };

  const branches = branchesData?.data || [];
  const isLoadingBranch = isLoading;
  const totalCount = filteredBranches?.length || 0;
  const totalPages = branchesData?.meta?.totalPages || 1;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="space-y-6">
      <Tabs
        defaultValue="active"
        className="space-y-4"
        onValueChange={(value) => {
          setStatusFilter(value === 'active' ? 'active' : 'all');
          setCurrentPage(1);
        }}
      >
        <TabsList>
          <TabsTrigger value="active">Active Branches</TabsTrigger>
          <TabsTrigger value="all">All Branches</TabsTrigger>
        </TabsList>
        <Card>
          <CardHeader>
            <CardTitle>Branch Management</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search branches..."
                    value={searchTerm}
                    onChange={(e) =>
                      handleFilterChange(() => setSearchTerm(e.target.value))
                    }
                    className="pl-10"
                  />
                </div>
                <Select
                  value={itemsPerPage.toString()}
                  onValueChange={(value) => {
                    setItemsPerPage(parseInt(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Items per page" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="25">25 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={() => handleBranchAction(null, 'add')}
                  className="whitespace-nowrap"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Branch
                </Button>
              </div>

              {/* Items per page and results info */}
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, totalCount)} of{' '}
                  {totalCount} results
                </div>
              </div>

              {/* Table */}
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Branch</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Members</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="hidden xl:!table-cell">
                        Created At
                      </TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isLoadingBranch ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-4">
                          Loading branches...
                        </TableCell>
                      </TableRow>
                    ) : isError ? (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center py-4 text-red-500"
                        >
                          Error loading branches. Please try again.
                        </TableCell>
                      </TableRow>
                    ) : branches.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-4">
                          No branches found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      branches.map((branch) => (
                        <TableRow key={branch.id}>
                          <TableCell>
                            <div className="flex items-center space-x-3">
                              <div className="w-10 h-10 rounded-md bg-gray-100 flex items-center justify-center">
                                {branch.imageUrls.length > 0 ? (
                                  <ImageWithFallback
                                    src={
                                      branch?.imageUrls[0]
                                        ? branch?.imageUrls[0]
                                        : ''
                                    }
                                    alt={branch.name}
                                    width={40}
                                    height={40}
                                  />
                                ) : null}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center">
                                  <div>
                                    <div className="font-medium">
                                      {branch.name}
                                    </div>
                                    <div className="text-sm text-muted-foreground">
                                      {branch.address}
                                    </div>
                                    {/* Created At for small screens */}
                                    {branch.createdAt && (
                                      <div className="text-xs text-muted-foreground xl:hidden">
                                        {new Date(
                                          branch.createdAt
                                        ).toLocaleDateString()}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <Phone className="h-4 w-4 mr-2 text-muted-foreground" />
                              {branch.phoneNumber || 'N/A'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <Users className="h-4 w-4 mr-2 text-muted-foreground" />
                              {branch.members.length}{' '}
                              {branch.members.length === 1
                                ? 'member'
                                : 'members'}
                            </div>
                          </TableCell>
                          <TableCell>
                            {branch.isActive ? (
                              <Badge
                                variant="outline"
                                className="bg-green-50 text-green-700 border-green-200"
                              >
                                Active
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="bg-gray-100 text-gray-700 border-gray-200"
                              >
                                Inactive
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="hidden xl:!table-cell">
                            <div className="text-sm text-muted-foreground">
                              {new Date(branch.createdAt).toLocaleDateString()}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-end space-x-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleBranchAction(branch, 'view')
                                }
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleBranchAction(branch, 'edit')
                                }
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handleBranchAction(branch, 'delete')
                                }
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-end mt-4">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </Tabs>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        open={!!branchToDelete}
        onOpenChange={(open) => !open && setBranchToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Branch"
        description="Are you sure you want to delete this branch? This action cannot be undone."
        isLoading={deleteBranchMutation.isPending}
      />
    </div>
  );
}
